import { useRef, useState } from 'react'
import { useMutation } from 'react-apollo'
import { useStripe } from '@stripe/react-stripe-js'
import {
  CellularPayPrompt,
  CellularPayResult,
  isBankChoice,
  PaymentFailure,
  PaymentMethodChoice
} from './types'
import { usePayInvoice } from './pay-invoice'
import { AMOUNT_CHANGED_FAILURE, isAmountChanged } from './amount-changed'
import { isAchStarted, isHolderNameReady } from './ach'
import { logInDev } from './log'
import {
  PW_CREATE_CELLULAR_ACH_SETUP_INTENT,
  PW_PAY_CELLULAR_INVOICE_WITH_BANK_ACCOUNT
} from '../../../apollo/api/paywall/mutations'

const GENERIC_FAILURE = 'We could not start your bank payment. Please try again.'

// The mutation resolved with no payload: we cannot tell whether the bank payment started.
export const BANK_NO_RESULT_FAILURE =
  'We did not get an answer from our payment service, so we cannot tell whether your bank ' +
  'payment started. Please reload the page and check before trying again.'

const BANK_FORM_LOADING = 'The bank connection is still loading. Please try again in a moment.'

interface UseBankPaymentArgs {
  accountId: string
  prompt: CellularPayPrompt
  method: PaymentMethodChoice
  // The account holder's name; Stripe needs it to add a bank account.
  holderName: string
  email?: string | null
  onPaid: () => void
  // A bank payment started, or is waiting for the test deposits: the bill counts as paid for now.
  onStarted: (result: CellularPayResult) => void
}

// What adding a bank account came to: its SetupIntent, the customer closing the
// bank window (not a failure), or a failure already shown.
type Added = { kind: 'added'; setupIntentId: string } | { kind: 'cancelled' } | { kind: 'reported' }

// The paywall's answer (null when it gave none), or a stop: the customer was already
// told why, or closed the bank window.
type Answer = { kind: 'answer'; result: CellularPayResult | null } | { kind: 'stop' }

export const useBankPayment = ({
  accountId,
  prompt,
  method,
  holderName,
  email,
  onPaid,
  onStarted
}: UseBankPaymentArgs) => {
  const stripe = useStripe()
  const [createSetupIntent] = useMutation(PW_CREATE_CELLULAR_ACH_SETUP_INTENT)
  const [payWithBankAccount] = useMutation(PW_PAY_CELLULAR_INVOICE_WITH_BANK_ACCOUNT)
  const payInvoice = usePayInvoice(accountId, prompt)
  const [paying, setPaying] = useState(false)
  const [failure, setFailure] = useState<PaymentFailure | null>(null)
  // A bank account added in this dialog whose payment got no answer (or hit an error):
  // a retry pays with it again rather than asking the customer to log in to their
  // bank twice. Dropped once the paywall refuses it, so the next Pay asks for another.
  const added = useRef<string | null>(null)
  const inFlight = useRef(false)

  const fail = (message: string) => setFailure({ message, hostedInvoiceUrl: null })

  const addBankAccount = async (): Promise<Added> => {
    if (added.current) return { kind: 'added', setupIntentId: added.current }
    if (!stripe) {
      fail(BANK_FORM_LOADING)
      return { kind: 'reported' }
    }
    const { data } = await createSetupIntent({
      variables: { accountId, invoiceId: prompt.invoiceId }
    })
    const setup = data?.createCellularAchSetupIntent
    if (!setup?.clientSecret) {
      fail(GENERIC_FAILURE)
      return { kind: 'reported' }
    }
    const billingDetails = { name: holderName.trim(), ...(email ? { email } : {}) }
    const collected = await stripe.collectBankAccountForSetup({
      clientSecret: setup.clientSecret,
      params: {
        payment_method_type: 'us_bank_account',
        payment_method_data: { billing_details: billingDetails }
      }
    })
    if (collected.error) {
      fail(collected.error.message || GENERIC_FAILURE)
      return { kind: 'reported' }
    }
    // The customer closed Stripe's bank window without choosing an account.
    if (collected.setupIntent?.status === 'requires_payment_method') return { kind: 'cancelled' }
    const confirmed = await stripe.confirmUsBankAccountSetup(setup.clientSecret)
    const status = confirmed.setupIntent?.status
    if (confirmed.error || (status !== 'succeeded' && status !== 'requires_action')) {
      fail(confirmed.error?.message || GENERIC_FAILURE)
      return { kind: 'reported' }
    }
    added.current = setup.setupIntentId
    return { kind: 'added', setupIntentId: setup.setupIntentId }
  }

  const payNewBank = async (): Promise<Answer> => {
    const bank = await addBankAccount()
    if (bank.kind !== 'added') return { kind: 'stop' }
    const { data } = await payWithBankAccount({
      variables: {
        accountId,
        invoiceId: prompt.invoiceId,
        setupIntentId: bank.setupIntentId,
        expectedAmountUsd: prompt.amountDueUsd
      }
    })
    return { kind: 'answer', result: data?.payCellularInvoiceWithBankAccount ?? null }
  }

  const paySavedBank = async (): Promise<Answer> => {
    const paymentMethodId = prompt.savedBankAccount?.paymentMethodId
    if (!paymentMethodId) return { kind: 'answer', result: null }
    const charge = await payInvoice(paymentMethodId)
    if (charge.kind === 'amountChanged') {
      setFailure(AMOUNT_CHANGED_FAILURE)
      return { kind: 'stop' }
    }
    return { kind: 'answer', result: charge.kind === 'result' ? charge.result : null }
  }

  // Returns true when the payment is done (or on its way) and this dialog is going away.
  const settle = (result: CellularPayResult | null): boolean => {
    if (!result) {
      fail(BANK_NO_RESULT_FAILURE)
      return false
    }
    if (result.paid) {
      onPaid()
      return true
    }
    if (isAchStarted(result)) {
      added.current = null
      onStarted(result)
      return true
    }
    // The paywall answered and refused this account: paying with it again would get the same.
    added.current = null
    fail(result.message || GENERIC_FAILURE)
    return false
  }

  const pay = async () => {
    // A second click must never start a second payment.
    if (!isBankChoice(method) || inFlight.current) return
    inFlight.current = true
    setPaying(true)
    setFailure(null)
    try {
      const answer = method === 'savedBank' ? await paySavedBank() : await payNewBank()
      if (answer.kind === 'answer' && settle(answer.result)) return
    } catch (error) {
      if (isAmountChanged(error)) {
        setFailure(AMOUNT_CHANGED_FAILURE)
      } else {
        logInDev('Failed to pay the cellular bill from a bank account', error)
        fail(GENERIC_FAILURE)
      }
    }
    inFlight.current = false
    setPaying(false)
  }

  // A new bank account needs Stripe.js ready to open the bank window, and a holder name.
  const canPay =
    !paying &&
    Number(prompt.amountDueUsd) > 0 &&
    (method === 'savedBank' || (method === 'bank' && !!stripe && isHolderNameReady(holderName)))

  return {
    pay,
    paying,
    failure,
    canPay,
    clearFailure: () => setFailure(null)
  }
}
